import React, { ComponentProps, ReactElement, ReactNode, Ref } from 'react';

import ControlContainer from './ControlContainer';
import Placeholder from './Placeholder';
import { defaultLabelGetter } from '../../helpers/defaultGetters';
import {
  defaultRenderItem,
  defaultRenderPlaceholder,
} from '../../helpers/defaultRenderers';
import { GenericItem, ItemToString } from '../../types';

export type ControlProps<TItem extends GenericItem> = ComponentProps<
  typeof ControlContainer<TItem>
> & {
  isLoading?: boolean;
  selectedItem: TItem | null;
  itemToString?: ItemToString<TItem>;
  placeholder?: string;
  renderInput?: () => ReactNode;
  renderPlaceholder?: (props?: ComponentProps<typeof Placeholder>) => ReactNode;
  renderItem?: ({
    selectedItem,
    itemToString,
    isOpen,
  }: {
    selectedItem?: TItem | null;
    itemToString: ItemToString<TItem>;
    isOpen: boolean;
  }) => ReactNode;
  highDensity?: boolean;
  highlightButtonOnFocus?: boolean;
  selectItem?: (item: TItem | null) => void;
};

const ControlWrapped = <TItem extends GenericItem>(
  {
    isOpen = false,
    selectedItem = null,
    renderInput,
    itemToString = defaultLabelGetter,
    renderPlaceholder = defaultRenderPlaceholder,
    renderItem = defaultRenderItem,
    placeholder,
    ...props
  }: ControlProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => (
  <ControlContainer ref={ref} isOpen={isOpen} {...props}>
    {renderInput && renderInput()}
    {selectedItem && renderItem({ selectedItem, itemToString, isOpen })}
    {!selectedItem && renderPlaceholder({ children: placeholder })}
  </ControlContainer>
);

const Control = React.forwardRef(ControlWrapped);

Control.displayName = 'Control';

Control.propTypes = {
  ...ControlContainer.propTypes,
};

export default Control as <T extends GenericItem>(
  p: ControlProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;
